import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { CockpitService, FeatureFlagService } from '../services';
import { CustomerFeatureFlag, FeatureFlag } from '../models';
import { AuthStore } from './auth.store';
import { SelectedCustomerStore } from './selected-customer.store';

interface FeatureFlagState {
  adminFeatureFlags: CustomerFeatureFlag[];
  customerFeatureFlags: CustomerFeatureFlag[];
  partnerFeatureFlags: CustomerFeatureFlag[];
  betaFeaturesEnabled: boolean;
}

const initialFeatureFlagState: FeatureFlagState = {
  adminFeatureFlags: [],
  customerFeatureFlags: [],
  partnerFeatureFlags: [],
  betaFeaturesEnabled: false,
};

export const FeatureFlagStore = signalStore(
  { providedIn: 'root' },
  withState<FeatureFlagState>(initialFeatureFlagState),
  withMethods(
    (
      store,
      featureFlagService = inject(FeatureFlagService),
      authStore = inject(AuthStore),
      selectedCustomerStore = inject(SelectedCustomerStore),
      cockpitService = inject(CockpitService),
    ) => {
      return {
        async loadFeatureFlags(): Promise<void> {
          if (authStore.user()?.isUser) {
            await this.loadCustomerFeatureFlags();
            return;
          }
          if (authStore.user()?.isPartner) {
            await this.loadPartnerFeatureFlags();
            return;
          }
          if (authStore.user()?.isAdmin) {
            try {
              const featureFlags = await firstValueFrom(featureFlagService.loadAdminFeatureFlags());
              patchState(store, { adminFeatureFlags: featureFlags });
            } catch (err) {}
          }
        },

        async loadCustomerFeatureFlags(customerId?: number): Promise<void> {
          try {
            const featureFlags = await firstValueFrom(featureFlagService.loadCustomerFeatureFlags(customerId));
            patchState(store, { customerFeatureFlags: featureFlags });
          } catch (err) {}
        },

        async loadPartnerFeatureFlags(partnerId?: string): Promise<void> {
          try {
            const featureFlags = await firstValueFrom(featureFlagService.loadPartnerFeatureFlags(partnerId));
            patchState(store, { customerFeatureFlags: featureFlags });
          } catch (err) {}
        },

        resetAllFeatureFlags() {
          patchState(store, {
            adminFeatureFlags: [],
            customerFeatureFlags: [],
            partnerFeatureFlags: [],
          });
        },

        resetCustomerFeatureFlags() {
          patchState(store, { customerFeatureFlags: [] });
        },

        resetPartnerFeatureFlags() {
          patchState(store, { partnerFeatureFlags: [] });
        },

        getFeatureFlag(featureName: string): CustomerFeatureFlag | undefined {
          let featureFlags: CustomerFeatureFlag[] = [];

          if (cockpitService.isAdmin()) {
            featureFlags = store.adminFeatureFlags();
          }
          if (cockpitService.isPartner()) {
            featureFlags = store.partnerFeatureFlags();
          }
          if (cockpitService.isCustomer()) {
            featureFlags = store.customerFeatureFlags();
          }
          return featureFlags.find((f) => f.name === featureName);
        },

        isFeatureEnabled(feature: FeatureFlag): boolean {
          const flag = this.getFeatureFlag(feature);
          return flag?.enabled ?? false;
        },

        canShowPreview(feature: FeatureFlag): boolean {
          const flag = this.getFeatureFlag(feature);
          return flag?.preview ?? false;
        },

        canShowFeature(feature: FeatureFlag): boolean {
          const flag = this.getFeatureFlag(feature);

          if (!flag || !flag.enabled) {
            return false;
          }

          const user = authStore.user();
          let customerBetaFeaturesEnabled: boolean = false;
          if (user && user.isUser && user.customer) {
            customerBetaFeaturesEnabled = user.customer!.betaFeaturesEnabled;
          } else {
            const selectedCustomer = selectedCustomerStore.selectedCustomer();
            customerBetaFeaturesEnabled = (selectedCustomer && selectedCustomer.betaFeaturesEnabled) ?? false;
          }

          return !flag.beta || customerBetaFeaturesEnabled;
        },

        isAnyFeatureVisible(features: FeatureFlag[]): boolean {
          return features.some((featureName) => this.canShowFeature(featureName) || this.canShowPreview(featureName));
        },
      };
    },
  ),
);
